
import React from 'react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Company, UserRole } from '@/lib/types';
import { addUserToCompany } from '@/services/userCompanyService';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface UserCompanySelectProps {
  userId: string;
  currentCompanyId: string | null;
  currentRole: UserRole;
  companies: Company[];
  onCompanyChange: (userId: string, companyId: string | null) => void;
}

export const UserCompanySelect: React.FC<UserCompanySelectProps> = ({ 
  userId, 
  currentCompanyId,
  currentRole,
  companies,
  onCompanyChange
}) => {
  const { toast } = useToast();

  const handleCompanyAssignment = async (value: string) => {
    try {
      const companyId = value === 'none' ? null : value;
      
      // First update the profile, preserving the current role
      const { error } = await supabase
        .from('profiles')
        .update({ 
          company_id: companyId,
          role: currentRole // Explicitly include the current role to preserve it
        })
        .eq('id', userId);

      if (error) throw error;

      if (companyId) {
        // Add user to company with their current role (not defaulting to 'member')
        await addUserToCompany(userId, companyId, currentRole);
      }

      onCompanyChange(userId, companyId);

      toast({
        title: "Company updated",
        description: companyId 
          ? `User has been assigned to a new company` 
          : `User has been removed from company`,
      });
    } catch (error: any) {
      toast({
        title: "Failed to update company",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <Select
      defaultValue={currentCompanyId || 'none'}
      onValueChange={handleCompanyAssignment}
    >
      <SelectTrigger className="w-[160px]">
        <SelectValue placeholder="No company" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">None</SelectItem>
        {companies.map((company) => (
          <SelectItem key={company.id} value={company.id}>
            {company.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
